import Dinero from 'dinero.js'
import { pickBy, range } from 'lodash'
import store from 'state/store'
import { setSnackBar } from 'state/ui'
import { Countries, Provinces } from 'country-and-province'
import Tooltip from 'components/generic/tooltip'

import { setIsClearingReduxFormState } from 'state/ui'

import { MEASURE_UNIT, ROUNDING_PRECISIONS, PAYMENT_METHODS_VALUES } from 'utils/constants'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { getTotalPriceDeduction, getTotalWeightDeduction } from 'utils/data-manipulation/common'
import { SelectItem } from 'components/generic/form/Select'
import dayjs from 'dayjs'

export const getUnitConversionFactors = () => {
  const { defaultYardUm } = store.getState()?.settings || {}

  const unitConversionFactors = {
    [MEASURE_UNIT.POUND]: 1,
    [MEASURE_UNIT.GROSS_TONS]: 2240,
    [MEASURE_UNIT.NET_TONS]: 2000,
    [MEASURE_UNIT.METRIC_TONS]: 2204.6,
    [MEASURE_UNIT.HUNDRED_WEIGHT]: 100,
    [MEASURE_UNIT.KILOGRAM]: 2.204622622,
  }

  unitConversionFactors[MEASURE_UNIT.EACH] = unitConversionFactors[defaultYardUm] || 1
  return unitConversionFactors
}

// TODO: Once enableNewInvoiceCalculations is removed, we need to replace this code with the new calculation logic
export const convertUM = (amount, fromUnit, toUnit, useNewInvoiceCalculations = false) => {
  const { defaultYardUm } = store.getState()?.settings || {}

  const METRIC_TONS_CONVERSION_FACTOR = 2204.622622

  const fromUnitLowercased = (fromUnit || defaultYardUm).toLowerCase()
  const toUnitLowercased = (toUnit || defaultYardUm).toLowerCase()

  if (fromUnitLowercased === toUnitLowercased) {
    return amount
  }

  const UNIT_CONVERSION_FACTORS = getUnitConversionFactors()

  const fromUnitConversionFactor =
    useNewInvoiceCalculations && fromUnitLowercased === MEASURE_UNIT.METRIC_TONS.toLowerCase()
      ? METRIC_TONS_CONVERSION_FACTOR
      : UNIT_CONVERSION_FACTORS[fromUnitLowercased] || 1
  const toUnitConversionFactor =
    useNewInvoiceCalculations && toUnitLowercased === MEASURE_UNIT.METRIC_TONS.toLowerCase()
      ? METRIC_TONS_CONVERSION_FACTOR
      : UNIT_CONVERSION_FACTORS[toUnitLowercased] || 1

  if (useNewInvoiceCalculations) {
    return roundTo((amount * fromUnitConversionFactor) / toUnitConversionFactor, ROUNDING_PRECISIONS.INTERNAL)
  } else {
    return (amount * fromUnitConversionFactor) / toUnitConversionFactor
  }
}

export const isDateInPast = timestamp => {
  if (!timestamp) {
    return false
  }

  const currentTimestamp = new Date().getTime()
  return currentTimestamp > timestamp
}

/** Converts a number to a string with the exact number of decimal places so that it can be displayed to the used.
 *  Throws an error if doing that would lose precision.
 **/
export const losslessToFixed = (number: number, precision: number) => {
  const fixedPrecision = number.toFixed(precision)
  if (Math.abs(Number(fixedPrecision) - number) > 2e-10) {
    throw new Error(`Loss of precision when converting ${number} to ${fixedPrecision}`)
  }
  return formatNumber(fixedPrecision)
}

export const unformatStringNumber = stringNumber => {
  if (!stringNumber) return
  if (typeof stringNumber !== 'string') return stringNumber
  return Number(stringNumber?.replace(/,/g, ''))
}

export const formatNumber = number =>
  number?.toString().replace(/^[+-]?\d+/, int => int.replace(/(\d)(?=(\d{3})+$)/g, '$1,'))

export const setDecimalPrecision = (
  number,
  settings: { decimalPrecisionOverride?: number; shouldFormatNumber?: boolean; removeTrailingZeros?: boolean } = {},
) => {
  const { decimalPrecisionOverride, shouldFormatNumber = true, removeTrailingZeros = false } = settings
  const newDecimalPrecision =
    decimalPrecisionOverride || decimalPrecisionOverride === 0
      ? decimalPrecisionOverride
      : store.getState()?.settings?.settings?.purchaseSettings?.inboundTickets?.decimalPrecision || 2

  let newValue: number | string = 0

  if (number) {
    const unformattedNumber = unformatStringNumber(number)
    newValue = roundTo(unformattedNumber, newDecimalPrecision)
  }

  if (removeTrailingZeros) {
    newValue = +parseFloat(newValue as any)
  } else {
    newValue = (newValue as any).toFixed(newDecimalPrecision)
  }

  if (shouldFormatNumber) {
    newValue = formatNumber(newValue)
  }

  return newValue
}

export const calcAdjustments = material => {
  try {
    const { defaultYardUm } = store.getState()?.settings || {}

    const { gross, tare, deductions, netPrice, um } = material?.weightAndPrice || {}
    const formattedGross = Number(gross?.amount || 0)
    const formattedTare = Number(tare?.amount || 0)
    const formattedNetWeight = roundTo(formattedGross - formattedTare, ROUNDING_PRECISIONS.WEIGHT)
    const trueValue = roundTo(formattedNetWeight * Dinero({ ...netPrice }).toUnit(), ROUNDING_PRECISIONS.COST) || 0

    const weightAdjustments = getTotalWeightDeduction(deductions, formattedNetWeight) || 0
    const priceAdjustments = getTotalPriceDeduction(deductions, convertUM(trueValue, defaultYardUm, um)) || 0
    return { weightAdjustments, priceAdjustments }
  } catch (error) {
    return { weightAdjustments: 0, priceAdjustments: 0 }
  }
}

export const getStatesAndProvinces = countries =>
  countries?.reduce((aggregate, country) => {
    const data = (Countries as any)
      .byName(country)
      ?.provinces?.data?.map(state => ({ id: state.code, name: state.code }))
    const cleanedData = country === 'United States' ? data?.slice(0, 51) : data
    return [...aggregate, { id: country, name: country, isHeader: true }, ...cleanedData]
  }, []) || []

export const getVehiclePlateStates = (locale: string): SelectItem[] => {
  // Currently only support en-US and en-NZ locales.
  if (locale === 'en-NZ') {
    // NZ doesn't use states in plates, so just use NZ as the state
    return [
      { id: 'New Zealand', name: 'New Zealand', isHeader: true },
      { id: 'NZ', name: 'NZ' },
    ]
  }
  // US and Canada states use regular states/provinces for plates
  const usAndCanadaStates = getStatesAndProvinces(['United States', 'Canada'])
  // Mexico numbers their states for plates, 01-32
  const mexicoStates = [
    { id: 'Mexico', name: 'Mexico', isHeader: true },
    ...range(1, 33).map(i => {
      const stateName = i.toString().padStart(2, '0')
      return {
        id: stateName,
        name: stateName,
      }
    }),
  ]
  return [...usAndCanadaStates, ...mexicoStates]
}

export const getStatesAndProvincesV2 = (
  countries: string[],
  options: {
    byCode?: boolean
    removeCountryHeaders?: boolean
  } = {},
): SelectItem[] =>
  countries.reduce<SelectItem[]>((aggregate, country) => {
    const { code: countryCode, name: countryName } = options.byCode
      ? Countries.byCode(country)
      : Countries.byName(country)
    const data = Provinces.byCountryCode(countryCode)?.map(state => ({ id: state.code, name: state.code }))
    const cleanedData = countryCode === 'US' ? data?.slice(0, 51) : data
    const newAggregate = [...aggregate]
    if (!options.removeCountryHeaders) {
      newAggregate.push({ id: countryCode, name: countryName, isHeader: true })
    }
    newAggregate.push(...cleanedData)
    return newAggregate
  }, []) || []

export const getCountries = (): SelectItem[] =>
  Countries.data
    .map(country => ({
      id: country.code,
      name: country.name,
      value: country.code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

export const getCountryFromState = (state?: string): string | undefined => {
  if (!state) return undefined
  return Countries.data.find(country => {
    return Provinces.byCountryCode(country.code).some(province => province.code === state)
  })?.code
}

// Strip out all values with empty strings
export const sanitizedValues = values =>
  pickBy(values, value =>
    value === null ||
    value === undefined ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'object'
      ? true
      : value.length > 0,
  )

export const makeMaterialTypeToolTip = materials => {
  const materialsList = materials.map(material => material.materialName)
  return (
    <Tooltip content={materialsList.join(', ')}>
      <>{materialsList.length > 1 ? 'Mixed' : materialsList[0]}</>
    </Tooltip>
  )
}

export const b64toBlob = (dataURI, blobType = 'image/jpeg') => {
  const byteString = atob(dataURI?.split(',')?.[1] || '')
  const newArrayBuffer = new ArrayBuffer(byteString.length)
  const intArray = new Uint8Array(newArrayBuffer)

  for (let i = 0; i < byteString.length; i++) {
    intArray[i] = byteString.charCodeAt(i)
  }
  return new Blob([newArrayBuffer], { type: blobType })
}

export const onKeyPressNumberField = e => {
  const regExp = /[0-9.]+/g
  if (!regExp.test(e.key)) {
    e.preventDefault()
  }
}

export const dispatchSnackbar = (dispatch, type, message) => {
  // use setTimeout here so that if the snackbar is already open, this setSnackBar will execute after the AlertSnackbar component calls handleClose
  setTimeout(() => {
    dispatch(
      setSnackBar({
        snackbarType: type,
        snackbarMessage: message,
      }),
    )
  }, 0)
}

export const downloadURI = (uri, name) => {
  const link = document.createElement('a')
  link.download = name
  link.href = uri
  link.click()
}

export const downloader = (data, type, name) => {
  const blob = new Blob([data], { type })
  const url = window.URL.createObjectURL(blob)
  downloadURI(url, name)
}

export const getToday = () => new Date()
export const getYesterday = () => {
  const yesterday = new Date()
  yesterday.setDate(getToday().getDate() - 1)
  return yesterday
}

const getLocalToYardTimezoneOffset = yardTimeZone => {
  const str = new Date().toLocaleString('en', { timeZone: yardTimeZone, timeZoneName: 'longOffset' })
  const [, h, m] = str.match(/([+-]\d+):(\d+)$/) || [null, '+00', '00']
  const yardTimezoneOffset = -1 * (Number(h) * 60 + (Number(h) > 0 ? +m : -m))
  const localTimezoneOffset = new Date().getTimezoneOffset()
  return yardTimezoneOffset - localTimezoneOffset
}

const getTimeInDifferentTimezone = (localTime, yardTimeZone) => {
  const timezoneOffset = getLocalToYardTimezoneOffset(yardTimeZone)

  const yardTime = new Date(localTime)
  yardTime.setMinutes(yardTime.getMinutes() + timezoneOffset)
  return yardTime
}

export const buildFilter = (_startDate, _endDate, defaultYardTimezone) => {
  const startDate = _startDate ? new Date(_startDate) : undefined

  if (startDate) {
    startDate.setHours(0, 0, 0, 0)
  }

  const endDate = _endDate ? new Date(_endDate) : undefined

  if (endDate) {
    endDate.setHours(23, 59, 59, 999)
  }

  if (defaultYardTimezone) {
    return JSON.stringify({
      date:
        startDate && dayjs(startDate).isValid()
          ? getTimeInDifferentTimezone(startDate, defaultYardTimezone)?.toISOString()
          : undefined,
      endDate:
        endDate && dayjs(endDate).isValid()
          ? getTimeInDifferentTimezone(endDate, defaultYardTimezone)?.toISOString()
          : undefined,
    })
  }

  return JSON.stringify({
    date: dayjs(startDate).isValid() ? startDate?.toISOString() || undefined : undefined,
    endDate: dayjs(endDate).isValid() ? endDate?.toISOString() || undefined : undefined,
  })
}

/**
 * Parses a number object and returns a formatted string based on the provided options.
 */
export const parseNumberObject = (numberObject, options = {} as any) => {
  const { defaultYardUm } = store.getState()?.settings || {}

  const {
    format = 'none', // ['none', 'weight', 'price', 'value']
    enableFourDecimalPrecisionInInventory = false,
    unitOverride,
  } = options

  const value = numberObject?.precision ? Dinero({ ...numberObject }).toUnit() : numberObject?.amount || 0

  if (format === 'none') return value

  const unit = unitOverride || numberObject?.units || defaultYardUm

  if (format === 'weight') {
    return `${setDecimalPrecision(value, { decimalPrecisionOverride: 1, removeTrailingZeros: true })} ${unit}`
  }
  return `$${setDecimalPrecision(value, {
    decimalPrecisionOverride: enableFourDecimalPrecisionInInventory ? 4 : 2,
  })}${format === 'price' ? `/${unit}` : ''}`
}

export const FORMAT_TYPES = {
  WEIGHT: 'weight',
  PRICE: 'price',
  PRICE_PER_UNIT: 'pricePerUnit',
  DEFAULT: 'default',
}

/**
 * Parses a value and returns a formatted string based on the provided options.
 * Focused on formatting numbers to positive or negative values with ().
 * @param {number | object} value - The value to format.
 * @param {object} options - The options object.
 */
export const formatNumberForDisplay = (value, options = {} as any) => {
  const { defaultYardUm } = store.getState()?.settings || {}

  let numberValue = value
  const { format = FORMAT_TYPES.DEFAULT, decimalPrecisionOverride = 1, currencySymbol = '$' } = options

  const isDineroObject = typeof numberValue === 'object' && numberValue.precision
  if (isDineroObject) {
    numberValue = Dinero({ ...numberValue }).toUnit()
  }
  const isPositive = numberValue >= 0
  const absoluteValue = Math.abs(numberValue)

  let valueFormatted = ''

  if (format === FORMAT_TYPES.WEIGHT) {
    valueFormatted = `${setDecimalPrecision(absoluteValue, {
      decimalPrecisionOverride,
      removeTrailingZeros: true,
    })} ${defaultYardUm}`
  } else if (format === FORMAT_TYPES.PRICE || format === FORMAT_TYPES.PRICE_PER_UNIT) {
    valueFormatted = `${currencySymbol}${setDecimalPrecision(absoluteValue, {
      decimalPrecisionOverride,
    })}`
    if (format === FORMAT_TYPES.PRICE_PER_UNIT) {
      valueFormatted += `/${defaultYardUm}`
    }
  } else {
    valueFormatted = setDecimalPrecision(absoluteValue, {
      decimalPrecisionOverride,
    }).toString()
  }
  return isPositive ? valueFormatted : `(${valueFormatted})`
}

/**
 * This tells us whether the location id provided is one of the yard's locations
 */
export const isLocationInYardList = (locId, yardDetailsList) => {
  return yardDetailsList?.some(yard => {
    return yard.defaultLocationId ? yard.defaultLocationId === locId : yard.locationId === locId
  })
}

/**
 * This tells us if the timestamp is today, disregarding time.
 * If timestamp2 is provided, it returns if the dates are the same.
 */
export const isDateToday = (timestamp1, timestamp2 = undefined) => {
  if (!timestamp1) return false

  const date1 = new Date(timestamp1)
  date1.setHours(0, 0, 0, 0)
  const time1 = date1.getTime()

  const date2 = timestamp2 ? new Date(timestamp2) : new Date()
  date2.setHours(0, 0, 0, 0)
  const time2 = date2.getTime()

  return time1 === time2
}

/**
 * @param payment a payment in the style of formattedPayment
 * @returns whether this payment is a printable check payment
 */
export const isPrintableCheckPayment = payment => {
  if (!payment) return false
  return (
    payment.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase() && // is a check
    !payment.checkNumber && // not printed yet
    (payment.total > 0 || payment.total?.amount > 0) // positive amount
  )
}

export const locationIsNotEmpty = location => {
  if (!location) return false
  return !!(
    location.address ||
    location.address1 ||
    location.address2 ||
    location.city ||
    location.name ||
    location.locationName ||
    location.state ||
    location.zip
  )
}

export const contactIsNotEmpty = contact => {
  if (!contact) return false
  return !!(contact.firstName || contact.lastName)
}

export const constructRecurrenceRules = (frequency, interval?, dayOfTheWeek?) => {
  return {
    frequency: frequency,
    interval: interval,
    dayOfTheWeek: dayOfTheWeek,
  }
}

export const clearReduxFormState = dispatch => {
  dispatch(setIsClearingReduxFormState(true))
  setTimeout(() => {
    dispatch(setIsClearingReduxFormState(false))
  }, 1000)
}

export const checkIfDateIsLocked = (lockAccountingPeriod, entityDate) => {
  if (!entityDate || !lockAccountingPeriod.lockAccountingDate) return false
  return new Date(entityDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
}

export const generateLockTooltip = (entityType = 'entity', showFullMessage = true) =>
  `This ${entityType} has an effective date inside the locked period.${showFullMessage ? ' If you make changes to materials or pricing, these will be reflected in inventory on the date of action.' : ''}`

export const getDateFormatPatternFromLocale = (locale: string) => {
  const formatter = new Intl.DateTimeFormat(locale)
  const parts = formatter.formatToParts(new Date(2025, 0, 31)) // Jan 31, 2025
  const dateFormat = parts
    .map(part => {
      switch (part.type) {
        case 'day':
          return 'DD'
        case 'month':
          return 'MM'
        case 'year':
          return 'YYYY'
        default:
          return part.value
      }
    })
    .join('')

  const muiDateFormat = parts
    .map(part => {
      switch (part.type) {
        case 'day':
          return 'dd'
        case 'month':
          return 'MM'
        case 'year':
          return 'yyyy'
        default:
          return part.value
      }
    })
    .join('')

  return { dateFormat, muiDateFormat }
}

/**
 * Formats fee or charge values for display using accounting conventions.
 * Negative values (fees/charges) are displayed as positive amounts in parentheses.
 * Positive values (promotions/credits) are displayed normally.
 */
export const formatFeeOrChargeValue = (value, pricePrepend = '$', isNegativeType = false, decimalPrecision = 2) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0

  const displayValue = isNegativeType ? Math.abs(numericValue) : numericValue
  const formattedValue = `${pricePrepend}${setDecimalPrecision(displayValue, {
    decimalPrecisionOverride: decimalPrecision,
  })}`
  if (isNegativeType || numericValue < 0) {
    return `(${formattedValue})`
  }

  return formattedValue
}
